import { z } from 'zod';

// GitHub login rules: 1–39 chars, alphanumeric or single hyphens, may not begin
// or end with a hyphen and may not contain consecutive hyphens. Matched
// case-insensitively. This is stricter than the org-slug check (`[a-z0-9-]+`),
// which tolerates leading/trailing/double hyphens and any length.
const GITHUB_LOGIN_RE = /^[\da-z](?:[\da-z]|-(?=[\da-z])){0,38}$/i;

/** A syntactically valid GitHub username/login. */
export const GithubLoginSchema = z
  .string()
  .refine((value) => GITHUB_LOGIN_RE.test(value), {
    message: 'Invalid GitHub login',
  });
export type GithubLogin = z.infer<typeof GithubLoginSchema>;

/** True when `value` is a syntactically valid GitHub login. */
export function isValidGithubLogin(value: string): boolean {
  return GITHUB_LOGIN_RE.test(value);
}

// An App's actor login carries a `[bot]` suffix — `coderabbitai[bot]`,
// `codecov[bot]`, `dependabot[bot]` — which `GITHUB_LOGIN_RE` rejects, since
// brackets are not legal in a *user* login.
const BOT_LOGIN_RE = /^[\da-z](?:[\da-z]|-(?=[\da-z])){0,38}\[bot]$/i;

/**
 * Any login that can ACT on a repository: a person, or an App.
 *
 * Distinct from {@link GithubLoginSchema}, which is for logins we might address
 * — a bot has no inbox. Use this wherever the value is "who did this", because
 * validating an actor with the human-only pattern silently discards every bot
 * event, and in a repo with CodeRabbit, Codecov and Linear installed that is
 * the majority of all activity.
 */
export const ActorLoginSchema = z
  .string()
  .refine((value) => GITHUB_LOGIN_RE.test(value) || BOT_LOGIN_RE.test(value), {
    message: 'Invalid GitHub actor login',
  });
export type ActorLogin = z.infer<typeof ActorLoginSchema>;

/** True when `value` looks like a GitHub App actor rather than a person. */
export function isBotLogin(value: string): boolean {
  return BOT_LOGIN_RE.test(value);
}

/**
 * The "me" people token. Default people selection; resolved server-side to the
 * PAT's own account via GitHub search's `@me`, so "me" is a mode, not a stored
 * login. A people selection is a list of these tokens.
 */
export const ME_PERSON = '@me';

/** True when `token` is a usable people token: the `@me` sentinel or a login. */
export function isValidPersonToken(token: string): boolean {
  return token === ME_PERSON || isValidGithubLogin(token);
}

/**
 * UI cap on how many people a selection may contain. A soft guard so the picker
 * stays usable; the server independently rejects selections that would exceed the
 * per-bucket query budget (`exceedsQueryBudget`).
 */
export const MAX_PEOPLE = 25;
